import { Box, Skeleton, Stack } from "@mui/material";
import { CompanyDataDisplay } from "Components/UserAccount/Shared/Party/CompanyDataDisplay";
import { AddressDisplay } from "Components/UserAccount/Shared/AddressDisplay";
import { DocumentsDisplay } from "Components/UserAccount/Shared/DocumentsDisplay";
import type { PartyDto } from "Infrastructure/Api/Api";
import { PartyType } from "Infrastructure/Api/Api";
import { InfoBox } from "Components/Shared/InfoBox/InfoBox";
import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { Resources, useResource } from "Translations/Resources";
import { usePartyActingPersonParty } from "Hooks/useUserActingPersonParty";

const RESOURCES = Resources.Components.Account;

type Props = {
  party: PartyDto;
};

export const HunterPartyDetail = ({ party }: Props) => {
  const { t } = useResource();
  const { data: verification } = useVerificationsQuery();
  const { actingPartyData, isLoading: isActingPersonLoading } =
    usePartyActingPersonParty(party.publicID ?? undefined);

  const { type, addresses } = party;
  const isSelfEmployed = type === PartyType.SelfEmployed;
  const isLegalEntity = type === PartyType.LegalEntity;
  const isDocumentExpired =
    verification?.identityVerificationStatus === "Expired";

  // LegalEntity hunters have their ID documents on the acting person (StatutoryBody/…),
  // not on the LE party itself. Resolve the docs through the related party.
  const docsParty = isLegalEntity ? actingPartyData : party;
  const docsPartyIdentification = docsParty?.partyIdentification;
  const docsPartyPublicID = docsParty?.publicID;

  const shouldShowDocuments =
    !!docsPartyIdentification &&
    (isSelfEmployed ||
      isLegalEntity ||
      !!docsPartyIdentification.identificationValidTo);

  const isResolvingDocs = isLegalEntity && isActingPersonLoading;

  return (
    <Stack>
      <Box mb={[2, 2, 4]}>
        <InfoBox
          variant="info"
          content={t(RESOURCES.PersonalDataChangeNotification)}
        />
      </Box>
      <CompanyDataDisplay party={party} />
      <AddressDisplay addresses={addresses} />
      {isResolvingDocs && (
        <Skeleton height={300} variant="rounded" sx={{ mt: "0.5rem" }} />
      )}
      {!isResolvingDocs && shouldShowDocuments && (
        <DocumentsDisplay
          partyIdentification={docsPartyIdentification}
          partyPublicID={docsPartyPublicID ?? undefined}
          isDocumentExpired={isDocumentExpired}
        />
      )}
    </Stack>
  );
};
